import { oc } from "@orpc/contract";
import { z } from "zod";

const RebateCsvTransformSessionStatusSchema = z.enum([
  "pending_sheet_selection",
  "queued",
  "running",
  "waiting_manual",
  "ready_for_review",
  "completed",
  "failed",
  "cancelled",
]);

export const CreateRebateCsvTransformSessionRequestSchema = z.object({
  fileName: z.string().describe("Original file name of the source file"),
  contentType: z
    .string()
    .default("application/octet-stream")
    .describe("MIME type of the file"),
  fileBase64: z
    .string()
    .describe("Base64-encoded file content to upload to blob storage"),
  fileNames: z
    .array(z.string())
    .optional()
    .describe("All file names if multiple were selected"),
  sourceContext: z.record(z.string(), z.unknown()).optional(),
  selectedSheet: z
    .string()
    .optional()
    .describe(
      "Sheet name to use for XLSX files. If omitted for multi-sheet XLSX, session enters pending_sheet_selection status.",
    ),
});

export const SelectRebateCsvTransformSheetRequestSchema = z.object({
  sessionId: z.string().uuid(),
  sheetName: z.string().describe("Name of the XLSX sheet to use as input"),
});

export const SaveRebateCsvTransformSnapshotRequestSchema = z.object({
  sessionId: z.string().uuid(),
  stepLabel: z
    .string()
    .describe(
      "Step label matching workflow step: preprocess, map_fields, transform, finalize",
    ),
  csvContent: z
    .string()
    .describe("CSV string output of this phase to persist to blob storage"),
  state: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("Phase-specific state (constants, mappings, explanations, etc.)"),
  rowCountOutput: z.number().int().optional(),
});

export const SaveRebateCsvTransformSnapshotResponseSchema = z.object({
  snapshotId: z.string().uuid(),
  outputArtifactKey: z.string(),
});

export const StartRebateCsvTransformRequestSchema = z.object({
  sessionId: z
    .string()
    .uuid()
    .optional()
    .describe("Existing session to resume from (skips completed steps)"),
  inputArtifactKey: z
    .string()
    .optional()
    .describe("Blob key of uploaded CSV (within default container)"),
  documentId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "Document ID from upload; used to resolve inputArtifactKey if provided instead of inputArtifactKey",
    ),
  instructions: z.string().optional(),
  fileNames: z.array(z.string()).optional(),
  sourceContext: z.record(z.string(), z.unknown()).optional(),
});

export const RebateCsvTransformSessionResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  createdBy: z.string().uuid().nullable(),
  workflowDefinitionId: z.string().uuid().nullable(),
  workflowRunId: z.string().uuid().nullable(),
  sourceContext: z.record(z.string(), z.unknown()).nullable(),
  status: RebateCsvTransformSessionStatusSchema,
  autoRunRemainder: z.boolean(),
  currentStepId: z.string().uuid().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const CreateRebateCsvTransformSessionResponseSchema =
  RebateCsvTransformSessionResponseSchema;

export const GetRebateCsvTransformSessionRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const ListRebateCsvTransformSessionsRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  fromDate: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
});

export const ListRebateCsvTransformSnapshotsRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const RebateCsvTransformSnapshotResponseSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  stepLabel: z.string(),
  workflowRunId: z.string().uuid().nullable(),
  stepRunId: z.string().uuid().nullable(),
  attemptNumber: z.number(),
  state: z.record(z.string(), z.unknown()).nullable(),
  inputArtifactKey: z.string().nullable(),
  outputArtifactKey: z.string().nullable(),
  rowCountInput: z.number().nullable(),
  rowCountOutput: z.number().nullable(),
  errorMessage: z.string().nullable(),
  failed: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const GetRebateCsvTransformArtifactRequestSchema = z.object({
  sessionId: z.string().uuid(),
  artifactKey: z.string(),
});

export const GetRebateCsvTransformArtifactResponseSchema = z.object({
  csvContent: z.string(),
});

export const RewindRebateCsvTransformStepRequestSchema = z.object({
  sessionId: z.string().uuid(),
  stepLabel: z.string(),
  inputOverride: z.record(z.string(), z.unknown()).optional(),
});

export const ResumeAutoRunRemainderRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const CompleteRebateCsvTransformSessionRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const CheckDuplicateRebateCsvTransformSessionsRequestSchema = z.object({
  fileName: z
    .string()
    .describe("File name to check for existing active sessions"),
});

export const CheckDuplicateRebateCsvTransformSessionsResponseSchema = z.object({
  duplicates: z.array(
    z.object({
      id: z.string().uuid(),
      status: RebateCsvTransformSessionStatusSchema,
      createdAt: z.date(),
      createdByName: z.string().nullable(),
      fileNames: z.array(z.string()),
    }),
  ),
});

export const DeleteRebateCsvTransformSessionRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const rebateCsvTransformContracts = {
  createSession: oc
    .input(CreateRebateCsvTransformSessionRequestSchema)
    .output(CreateRebateCsvTransformSessionResponseSchema),

  saveSnapshot: oc
    .input(SaveRebateCsvTransformSnapshotRequestSchema)
    .output(SaveRebateCsvTransformSnapshotResponseSchema),

  start: oc
    .input(StartRebateCsvTransformRequestSchema)
    .output(RebateCsvTransformSessionResponseSchema),

  getSession: oc
    .input(GetRebateCsvTransformSessionRequestSchema)
    .output(RebateCsvTransformSessionResponseSchema.nullable()),

  listSessions: oc
    .input(ListRebateCsvTransformSessionsRequestSchema.optional())
    .output(z.array(RebateCsvTransformSessionResponseSchema)),

  listSnapshots: oc
    .input(ListRebateCsvTransformSnapshotsRequestSchema)
    .output(z.array(RebateCsvTransformSnapshotResponseSchema)),

  getArtifact: oc
    .input(GetRebateCsvTransformArtifactRequestSchema)
    .output(GetRebateCsvTransformArtifactResponseSchema),

  rewindStep: oc
    .input(RewindRebateCsvTransformStepRequestSchema)
    .output(z.object({ success: z.boolean() })),

  resumeAutoRun: oc
    .input(ResumeAutoRunRemainderRequestSchema)
    .output(z.object({ success: z.boolean() })),

  completeSession: oc
    .input(CompleteRebateCsvTransformSessionRequestSchema)
    .output(z.object({ success: z.boolean() })),

  selectSheet: oc
    .input(SelectRebateCsvTransformSheetRequestSchema)
    .output(RebateCsvTransformSessionResponseSchema),

  checkDuplicateSessions: oc
    .input(CheckDuplicateRebateCsvTransformSessionsRequestSchema)
    .output(CheckDuplicateRebateCsvTransformSessionsResponseSchema),

  deleteSession: oc
    .input(DeleteRebateCsvTransformSessionRequestSchema)
    .output(z.object({ success: z.boolean() })),
};
